// Version mobile responsive pour l'interface Professeur - Explications
import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
  useRequestsApi,
  type RequestResponse,
} from "../../../hooks/useRequestsApi";
import AssignedRequests from "./AssignedRequests";
import AssignSlotsModal from "./AssignSlotsModal";
import CreateMeetingModal from "./CreateMeetingModal";
import RequestDetailsModal from "./RequestDetailsModal";
import AddMeetLinkModal from "./AddMeetLinkModal";

// Composants mobiles
import MobileCard from "../../common/MobileComponents/MobileCard";

const MobileTeacherExplanations: React.FC = () => {
  const [assignedRequests, setAssignedRequests] = useState<RequestResponse[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCreateMeetingModal, setShowCreateMeetingModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAddMeetLinkModal, setShowAddMeetLinkModal] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<RequestResponse | null>(null);
  const [selectedStatus, setSelectedStatus] = useState("");

  // Utiliser le nouveau hook pour l'API
  const { getMyRequests, proposeSlots, createMeeting } = useRequestsApi();

  // Charger les données du professeur
  const fetchTeacherData = async () => {
    try {
      setLoading(true);
      const response = await getMyRequests();

      if (response.status === 200 && Array.isArray(response.body)) {
        const allRequests = response.body;
        setAssignedRequests(allRequests);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeacherData();
  }, []);

  // Assigner des créneaux
  const handleAssignSlots = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowAssignModal(true);
  };

  // Ouvrir la modale de création de réunion
  const handleCreateMeeting = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowCreateMeetingModal(true);
  };

  // Ouvrir le modal de détails
  const handleViewDetails = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  // Ouvrir le modal d'ajout de lien Meet
  const handleAddMeetLink = (request: RequestResponse | any) => {
    setSelectedRequest(request);
    setShowAddMeetLinkModal(true);
  };

  // Gestion du filtre de statut
  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(event.target.value);
  };

  // Fonctions de statut
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "validated":
        return "bg-blue-100 text-blue-800";
      case "slots_proposed":
        return "bg-yellow-100 text-yellow-800";
      case "student_confirmed":
        return "bg-green-100 text-green-800";
      case "meeting_scheduled":
        return "bg-purple-100 text-purple-800";
      case "scheduled":
        return "bg-indigo-100 text-indigo-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "validated":
        return "Nouvelle assignation";
      case "slots_proposed":
        return "Créneaux proposés";
      case "student_confirmed":
        return "Créneau confirmé";
      case "meeting_scheduled":
        return "Réunion programmée";
      case "scheduled":
        return "Programmée";
      case "completed":
        return "Terminée";
      case "rejected":
        return "Rejetée";
      default:
        return "Statut inconnu";
    }
  };

  // Filtrer les demandes
  const getFilteredRequests = () => {
    if (!assignedRequests) return [];

    let filteredRequests = assignedRequests;

    // Filtrer par statut spécifique (si sélectionné)
    if (selectedStatus) {
      filteredRequests = filteredRequests.filter(
        (r) => r.status === selectedStatus,
      );
    }

    return filteredRequests.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  };

  // Confirmer l'assignation de créneaux
  const handleAssignSlotsConfirm = async (
    slots: { startTime: Date; endTime: Date }[],
  ) => {
    if (!selectedRequest) return;

    try {
      // L'API attend un tableau de strings ISO (dates de début uniquement)
      const response = await proposeSlots(selectedRequest._id, {
        slots: slots.map((slot) => slot.startTime.toISOString()),
      });

      if (response.status === 200) {
        toast.success("Créneaux proposés avec succès !");
        await fetchTeacherData(); // Rafraîchir les données
        setShowAssignModal(false);
        setSelectedRequest(null);
      } else {
        toast.error("Erreur lors de la proposition de créneaux");
      }
    } catch (error) {
      console.error("Erreur lors de la proposition de créneaux:", error);
      toast.error("Erreur lors de la proposition de créneaux");
    }
  };

  const filteredRequests = getFilteredRequests();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header mobile */}
      <div className="bg-white border-b border-stroke sticky top-0 z-10">
        <div className="px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900">
            Mes assignations d'explications
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Consultez toutes les demandes qui vous ont été assignées
          </p>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Filtre de statut */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filtrer par statut
          </label>
          <select
            value={selectedStatus}
            onChange={handleStatusChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Tous les statuts</option>
            <option value="validated">Nouvelle assignation</option>
            <option value="slots_proposed">Créneaux proposés</option>
            <option value="student_confirmed">Créneau confirmé</option>
            <option value="meeting_scheduled">Réunion programmée</option>
            <option value="scheduled">Programmée</option>
            <option value="completed">Terminée</option>
          </select>
        </div>

        {/* Liste des demandes */}
        <MobileCard>
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Toutes les explications ({filteredRequests.length})
            </h2>

            <AssignedRequests
              requests={filteredRequests}
              onProposeSlots={handleAssignSlots}
              onViewDetails={handleViewDetails}
              onAddMeetLink={handleAddMeetLink}
              getStatusBadgeColor={getStatusBadgeColor}
              getStatusText={getStatusText}
            />
          </div>
        </MobileCard>
      </div>

      {/* Modale d'assignation de créneaux */}
      {showAssignModal && selectedRequest && (
        <AssignSlotsModal
          open={showAssignModal}
          request={selectedRequest}
          onAssign={handleAssignSlotsConfirm}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedRequest(null);
          }}
        />
      )}

      {/* Modale de création de réunion */}
      {showCreateMeetingModal && selectedRequest && (
        <CreateMeetingModal
          requestId={selectedRequest._id}
          open={showCreateMeetingModal}
          onClose={() => {
            setShowCreateMeetingModal(false);
            setSelectedRequest(null);
          }}
          onMeetingCreated={() => {
            fetchTeacherData(); // Rafraîchir les données
            setShowCreateMeetingModal(false);
          }}
        />
      )}

      {/* Modal de détails */}
      {showDetailsModal && selectedRequest && (
        <RequestDetailsModal
          open={showDetailsModal}
          request={selectedRequest}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedRequest(null);
          }}
          getStatusBadgeColor={getStatusBadgeColor}
          getStatusText={getStatusText}
        />
      )}

      {/* Modal d'ajout de lien Meet */}
      {showAddMeetLinkModal && selectedRequest && (
        <AddMeetLinkModal
          open={showAddMeetLinkModal}
          request={selectedRequest}
          onClose={() => {
            setShowAddMeetLinkModal(false);
            setSelectedRequest(null);
          }}
          onSuccess={() => {
            fetchTeacherData(); // Rafraîchir les données
            setShowAddMeetLinkModal(false);
          }}
        />
      )}
    </div>
  );
};

export default MobileTeacherExplanations;
